import React, { useCallback, useEffect, useState, useRef } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { fetchEmployeeExit } from "../../../Apis/Employee-api";
import LogoutIcon from "@mui/icons-material/Logout";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { fetchOrganizationEmploymenentExiReasonTpes } from "../../../Apis/Employment-exit-reason";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_COLUMNS = [
  {
    field: "employee_code",
    label: "Employee Code",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employee_name",
    label: "Employee Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "notice_period_start",
    label: "Notice Period Start",
    visible: true,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "notice_period_end",
    label: "Notice Period End",
    visible: true,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "resignation_date",
    label: "Resignation Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "relieving_date",
    label: "Relieving Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "exit_interview_done",
    label: "Exit Interview",
    visible: true,
    width: 130,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "exit_reason",
    label: "Exit Reason",
    visible: true,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "employee_exit_id",
  sortOrder: "desc",
};

const EXIT_TOOLBAR_FILTERS = [
  {
    urlKey: "exit_reason_category_id",
    field: "organization_employment_exit_reason_type_id",
  },
  { urlKey: "exit_interview_done", field: "exit_interview_done" },
];

const EXIT_INTERVIEW_OPTIONS = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
];

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  // Global search sent as request body "search" so backend searches all columns
  return filters;
};

function EmployeeExitList() {
  const [exit, setexit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [exitReasonTypes, setExitReasonTypes] = useState([]);

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  
  const date = new Date(dateStr);
  if (isNaN(date)) return ""; // avoid invalid date errors

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};


  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  };

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
           `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `employee_exits_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );  

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        } else {
          setConfigColumns(DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchOrganizationEmploymenentExiReasonTpes(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        setExitReasonTypes(
          asOptionList(data, ["exitreason", "exitreasontype", "data"])
        );
      })
      .catch((error) => {
        console.error("Failed to load exit reason categories", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // load EmployeeExit data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          EXIT_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "employee_exit_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-exits`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchEmployeeExit(org.organization_id);
          data = applyClientToolbarFilters(
            oldData?.employeexit?.data || [],
            searchParams,
            EXIT_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Keep the transformation logic unchanged
        let a = data;
        console.log("data is   ", a);
        let b = a.map((item) => {
          const interviewDone =
            item.exit_interview_done === 1 ||
            item.exit_interview_done === "1" ||
            item.exit_interview_done === true ||
            item.exit_interview_done === "true";

          return {
            ...item,
            id: item.employee_exit_id,
            exit_interview_done: interviewDone ? "✔" : "✖",
          };
        });
        setexit(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch employee exits:", err);
          toast.error("Failed to fetch employee exits");
          setexit([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const deleteExit = useCallback(
    async (id, options = {}) => {
      try {
        const restoreEmployee = options.restore_employee ? 1 : 0;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/employee-exit/${id}?restore_employee=${restoreEmployee}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Employee exit deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            data.message || data.error || "Failed to delete employee exit";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete employee exit"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/employee/employee-exits/edit/${item.id || item.employee_exit_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/employee/employee-exits/view/${item.id || item.employee_exit_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        add_action={"EMPLOYEE_EXIT_ADD"}
        heading={"Employee Exit"}
        btnName={"Add Employee Exit"}
        delete_action={"EMPLOYEE_EXIT_DELETE"}
        Data={exit}
        tableHeaders={[
          { name: "Code", value_key: "employee_code", width: "50px" },
          { name: "Name", value_key: "name", width: "150px" },

          {
            name: "NoticePeriod Starts",
            value_key: "notice_period_start",
            width: "170px",
          },

          {
            name: "NoticePeriod Ends",
            value_key: "notice_period_end",
            width: "170px",
          },
          {
            name: "Resignation Date",
            value_key: "resignation_date",
            width: "150px",
          },
          {
            name: "Relieving Date",
            value_key: "relieving_date",
            width: "150px",
          },
          {
            name: "Interview",
            value_key: "exit_interview_done",
            width: "50px",
          },
        ]}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Employee exit details",
          "Employee exit details",
          "Add Employee Exit Details",
          "Employee Exit Deatils",
        ]}
        Route={"/organization/employee/employee-exits"}
        setData={setexit}
        DeleteFunc={deleteExit}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="employee_exits"
          primaryKey="employee_exit_id"
          heading="Employee Exit"
          data={exit}
          sortname="employee_name"
          showActions={true}
          edit_delete_action={["EMPLOYEE_EXIT_EDIT", "EMPLOYEE_EXIT_DELETE"]}
          Route="/organization/employee/employee-exits"
          DeleteFunc={deleteExit}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="employee_exit"
          deleteRestoreOption={true}
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Exit Reason Category"
                urlKey="exit_reason_category_id"
                allLabel="All reason categories"
                options={exitReasonTypes}
                valueKey="organization_employment_exit_reason_type_id"
                labelKey="employment_exit_reason_type_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Exit Interview Done"
                urlKey="exit_interview_done"
                allLabel="All interview statuses"
                options={EXIT_INTERVIEW_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default EmployeeExitList;

